import { useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { BANNER_AUTOPLAY_INTERVAL_MS } from '../bannerCarousel.consts';

export const useBannerCarousel = (slideCount: number) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const hasMultipleSlides = slideCount > 1;
  const isAutoplayEnabled = hasMultipleSlides && !isHovered && !isFocused && !prefersReducedMotion;

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    track.scrollTo({
      left: index * track.clientWidth,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const handleScroll = () => {
    const track = trackRef.current;

    if (!track || track.clientWidth === 0) {
      return;
    }

    setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
  };

  useEffect(() => {
    if (!isAutoplayEnabled) {
      return;
    }

    const timer = window.setInterval(() => {
      const track = trackRef.current;

      if (!track) {
        return;
      }

      track.scrollTo({
        left: ((activeIndex + 1) % slideCount) * track.clientWidth,
        behavior: 'smooth',
      });
    }, BANNER_AUTOPLAY_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isAutoplayEnabled, activeIndex, slideCount]);

  return {
    trackRef,
    activeIndex,
    hasMultipleSlides,
    scrollToIndex,
    handleScroll,
    handleMouseEnter: () => setIsHovered(true),
    handleMouseLeave: () => setIsHovered(false),
    handleFocus: () => setIsFocused(true),
    handleBlur: () => setIsFocused(false),
  };
};
